<template>
  <div>
    <woot-modal-header :header-title="filterModalHeaderTitle">
      <p class="text-slate-600 dark:text-slate-200">
        {{ filterModalSubTitle }}
      </p>
    </woot-modal-header>
    <div class="p-8">
      <div v-if="isFolderView">
        <label class="input-label" :class="{ error: !activeFolderNewName }">
          {{ $t('FILTER.FOLDER_LABEL') }}
          <input
            v-model="activeFolderNewName"
            type="text"
            class="folder-input border-slate-75 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          />
          <span v-if="!activeFolderNewName" class="message">
            {{ $t('FILTER.EMPTY_VALUE_ERROR') }}
          </span>
        </label>
        <label class="mb-1">
          {{ $t('FILTER.FOLDER_QUERY_LABEL') }}
        </label>
      </div>
      <div
        class="p-4 rounded-lg bg-slate-25 dark:bg-slate-900 border border-solid border-slate-50 dark:border-slate-700/50 mb-4"
      >
        <div
          v-if="folderScope"
          class="folder-scope-row mb-3 flex items-center gap-2 p-2 rounded-md bg-white dark:bg-slate-800 border border-dashed border-slate-100 dark:border-slate-700"
        >
          <span
            class="text-xxs uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-300 px-1.5 py-0.5 rounded bg-slate-50 dark:bg-slate-700"
          >
            {{ $t('FILTER.SCOPE_LABEL') }}
          </span>
          <span class="folder-scope-condition flex items-center text-sm text-slate-700 dark:text-slate-100 gap-1.5">
            <span class="font-medium">{{ folderScope.attribute }}</span>
            <fluent-icon icon="arrow-right" size="14" class="text-slate-400" />
            <span class="font-semibold text-woot-600 dark:text-woot-300">
              {{ folderScope.value }}
            </span>
          </span>
          <fluent-icon
            v-tooltip.top="$t('FILTER.SCOPE_LOCKED_TOOLTIP')"
            icon="lock-closed"
            size="14"
            class="ml-auto text-slate-400"
          />
        </div>
        <filter-input-box
          v-for="(filter, i) in appliedFilters"
          :key="i"
          v-model="appliedFilters[i]"
          :filter-groups="filterGroups"
          :input-type="
            getInputType(
              appliedFilters[i].attribute_key,
              appliedFilters[i].filter_operator
            )
          "
          :operators="getOperators(appliedFilters[i].attribute_key)"
          :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
          :show-query-operator="i !== appliedFilters.length - 1"
          :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
          :grouped-filters="true"
          :v="$v.appliedFilters.$each[i]"
          @resetFilter="resetFilter(i, appliedFilters[i])"
          @removeFilter="removeFilter(i)"
        />
        <div class="mt-4">
          <woot-button
            icon="add"
            color-scheme="success"
            variant="smooth"
            size="small"
            @click="appendNewFilter"
          >
            {{ $t('FILTER.ADD_NEW_FILTER') }}
          </woot-button>
        </div>
      </div>
      <div class="w-full">
        <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
          <woot-button class="button clear" @click.prevent="onClose">
            {{ $t('FILTER.CANCEL_BUTTON_LABEL') }}
          </woot-button>
          <woot-button
            v-if="isFolderView"
            :disabled="!activeFolderNewName"
            @click="updateSavedCustomViews"
          >
            {{ $t('FILTER.UPDATE_BUTTON_LABEL') }}
          </woot-button>
          <woot-button class="apply" v-else @click="submitFilterQuery">
            {{ $t('FILTER.SUBMIT_BUTTON_LABEL') }}
          </woot-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import FilterInputBox from '../FilterInput/Index.vue';
import languages from './advancedFilterItems/languages';
import countries from 'shared/constants/countries.js';
import { mapGetters } from 'vuex';
import { filterAttributeGroups } from './advancedFilterItems';
import filterMixin from 'shared/mixins/filterMixin';
import * as OPERATORS from 'dashboard/components/widgets/FilterInput/FilterOperatorTypes.js';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';

export default {
  components: {
    FilterInputBox,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    initialFilterTypes: {
      type: Array,
      default: () => [],
    },
    initialAppliedFilters: {
      type: Array,
      default: () => [],
    },
    activeFolderName: {
      type: String,
      default: '',
    },
    isFolderView: {
      type: Boolean,
      default: false,
    },
    folderScope: {
      type: Object,
      default: null,
    },
  },
  validations: {
    appliedFilters: {
      required,
      $each: {
        values: {
          ensureRelativeTimeRange(value, prop) {
            if (prop.filter_operator === 'last_x_hours') {
              const hours = Number(value);
              return Number.isInteger(hours) && hours >= 1 && hours <= 24;
            }
            if (prop.filter_operator === 'days_before') {
              return parseInt(value, 10) > 0 && parseInt(value, 10) < 999;
            }
            return true;
          },
          required: requiredIf(prop => {
            return !(
              prop.filter_operator === 'is_present' ||
              prop.filter_operator === 'is_not_present'
            );
          }),
        },
      },
    },
  },
  data() {
    return {
      show: true,
      appliedFilters: this.initialAppliedFilters,
      activeFolderNewName: this.activeFolderName,
      filterTypes: this.initialFilterTypes,
      filterAttributeGroups,
      filterGroups: [],
      allCustomAttributes: [],
      attributeModel: 'conversation_attribute',
      filtersFori18n: 'FILTER',
    };
  },
  computed: {
    ...mapGetters({
      getAppliedConversationFilters: 'getAppliedConversationFilters',
      campaigns: 'campaigns/getAllCampaigns',
      campaignStatuses: 'campaigns/getCampaignStatuses',
    }),
    filterModalHeaderTitle() {
      return !this.isFolderView
        ? this.$t('FILTER.TITLE')
        : this.$t('FILTER.EDIT_CUSTOM_FILTER');
    },
    filterModalSubTitle() {
      return !this.isFolderView
        ? this.$t('FILTER.SUBTITLE')
        : this.$t('FILTER.CUSTOM_VIEWS_SUBTITLE');
    },
  },
  watch: {
    campaigns: {
      handler(newCampaigns) {
        if (newCampaigns && newCampaigns.length > 0) {
          this.transformCampaignFilterValues();
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.setFilterAttributes();
    this.$store.dispatch('campaigns/fetchCampaignFilterOptions');
    if (this.getAppliedConversationFilters.length) {
      this.appliedFilters = [];
      this.appliedFilters = [...this.getAppliedConversationFilters];
      // Transform campaign filter values from IDs to full objects
      this.transformCampaignFilterValues();
    } else if (this.isFolderView && this.initialAppliedFilters.length) {
      // For folder view, use initialAppliedFilters and transform them
      this.appliedFilters = [...this.initialAppliedFilters];
      this.transformCampaignFilterValues();
    } else if (!this.isFolderView) {
      this.appliedFilters = [
        {
          attribute_key: 'status',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
          attribute_model: 'standard',
        },
      ];
    }
  },
  methods: {
    transformCampaignFilterValues() {
      // Wait for campaigns to be loaded
      this.$nextTick(() => {
        this.appliedFilters.forEach((filter, index) => {
          if (filter.attribute_key === 'campaign_id' && filter.values) {
            const campaignId = Array.isArray(filter.values) ? filter.values[0] : filter.values;
            
            // Find the campaign object by ID (try both string and number comparison)
            const campaign = this.campaigns.find(c => {
              return c.id === campaignId || 
                     c.id === String(campaignId) || 
                     c.id === Number(campaignId) ||
                     String(c.id) === String(campaignId);
            });
            
            if (campaign) {
              // Transform the values to use the full campaign object
              // Preserve existing statusValues if they exist
              this.appliedFilters[index] = {
                ...filter,
                values: {
                  id: campaign.id,
                  name: campaign.name,
                  statuses_attributes: campaign.statuses_attributes || this.campaignStatuses
                },
                // Preserve statusValues from the saved filter
                statusValues: filter.statusValues || null
              };
            }
          }
        });
      });
    },
    transformCampaignFiltersForSaving(filters) {
      return filters.map(filter => {
        if (filter.attribute_key === 'campaign_id' && filter.values) {
          // If values is an object with id, extract just the id
          if (typeof filter.values === 'object' && filter.values.id) {
            return {
              ...filter,
              values: [filter.values.id],
              // Preserve statusValues when saving
              statusValues: filter.statusValues
            };
          }
          // If values is already an array of IDs, keep it as is but preserve statusValues
          return {
            ...filter,
            statusValues: filter.statusValues
          };
        }
        return filter;
      });
    },
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        case 'text':
          return 'plain_text';
        case 'list':
          return 'search_select';
        case 'checkbox':
          return 'search_select';
        default:
          return 'plain_text';
      }
    },
    getAttributeModel(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type.attributeModel;
    },
    getInputType(key, operator) {
      if (key === 'created_at' || key === 'last_activity_at')
        if (operator === 'days_before' || operator === 'last_x_hours')
          return 'plain_text';
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.inputType;
    },
    getOperators(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.filterOperators;
    },
    getDropdownValues(type) {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      const allCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ](this.attributeModel);

      const isCustomAttributeCheckbox = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type &&
          attr.attribute_display_type === 'checkbox'
        );
      });

      if (isCustomAttributeCheckbox) {
        return [
          {
            id: true,
            name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
          },
          {
            id: false,
            name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
          },
        ];
      }

      const isCustomAttributeList = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type && attr.attribute_display_type === 'list'
        );
      });

      if (isCustomAttributeList) {
        return allCustomAttributes
          .find(attr => attr.attribute_key === type)
          .attribute_values.map(item => {
            return {
              id: item,
              name: item,
            };
          });
      }

      switch (type) {
        case 'status':
          return [
            ...Object.keys(statusFilters).map(status => {
              return {
                id: status,
                name: statusFilters[status].TEXT,
              };
            }),
          ];
        case 'assignee_id':
          return this.$store.getters['agents/getAgents'];
        case 'contact':
          return this.$store.getters['contacts/getContacts'];
        case 'inbox_id':
          return this.$store.getters['inboxes/getInboxes'];
        case 'team_id':
          return this.$store.getters['teams/getTeams'];
        case 'campaign_id':
          if (!Array.isArray(this.campaigns)) {
            return [];
          }
          return this.campaigns.map(campaign => ({
            id: campaign.id,
            name: campaign.name,
            statuses_attributes: campaign.statuses_attributes || this.campaignStatuses,
          }));
        case 'labels':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.title,
              name: i.title,
            };
          });
        case 'browser_language':
          return languages;
        case 'country_code':
          return countries;
        default:
          return undefined;
      }
    },
    appendNewFilter() {
      if (this.isFolderView) {
        this.setQueryOperatorOnLastQuery();
      } else {
        this.appliedFilters.push({
          attribute_key: 'status',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      }
    },
    setQueryOperatorOnLastQuery() {
      const lastItemIndex = this.appliedFilters.length - 1;
      this.appliedFilters[lastItemIndex] = {
        ...this.appliedFilters[lastItemIndex],
        query_operator: 'and',
      };
      this.$nextTick(() => {
        this.appliedFilters.push({
          attribute_key: 'status',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      });
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
      }
    },
    submitFilterQuery() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      
      // Transform campaign filter values back to simple IDs for saving
      const transformedFilters = this.transformCampaignFiltersForSaving(this.appliedFilters);
      
      this.$store.dispatch(
        'setConversationFilters',
        JSON.parse(JSON.stringify(transformedFilters))
      );
      this.$emit('applyFilter', transformedFilters);
      this.$track(CONVERSATION_EVENTS.APPLY_FILTER, {
        applied_filters: transformedFilters.map(filter => ({
          key: filter.attribute_key,
          operator: filter.filter_operator,
        })),
      });
    },
    updateSavedCustomViews() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.showAlert(this.$t('FILTER.EMPTY_VALUE_ERROR'));
        return;
      }
      // Transform campaign filter values back to simple IDs for saving
      const transformedFilters = this.transformCampaignFiltersForSaving(this.appliedFilters);
      this.$emit('updateFolder', transformedFilters, this.activeFolderNewName);
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
  },
};
</script>
<style lang="scss" scoped>
.folder-input {
  @apply w-[50%];
}
.apply{
  background-color: #40518a !important;
}
</style>
